import { useCallback, useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useOpenApi } from '@/common/providers/open-api-provider';
import {
    getFallbackErrorKey,
    getRetryAfterSeconds,
    isApiError
} from '@/common/util';
import { UIToast } from '@/ui';
import { getAppCheckTokenHeaders, isFirebaseError } from '@/firebase';
import { useDevicesTranslation } from '../hooks';

type UseTestCallDeviceActionProps = {
    deviceId: string;
};

type UseTestCallDeviceActionData = {
    /** Resolves with the status id to poll with `useTestDeviceStatus`. */
    testCallDevice: () => Promise<string>;
    loading: boolean;
    error: string;
};

/**
 * Placing a test call and SMS to a device, to check it is reachable.
 *
 * Nothing is cached on success: the call returns only the id of the test to
 * poll, and the device itself is unchanged.
 *
 * No `isAllowed`/`reason`: the test button lives on the device settings page,
 * which has already decided whether this viewer may act on the device.
 */
export function useTestCallDeviceAction(
    props: UseTestCallDeviceActionProps
): UseTestCallDeviceActionData {
    const { deviceId } = props;

    const { t } = useDevicesTranslation();
    // The client is swapped out on token refresh, so never capture it: read
    // `openApiRef.current` at call time or you keep using a dead token.
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    // One place that turns a failure into a message, so the toast and the
    // returned `error` can never disagree. Every error
    // `POST /v3/devices/{deviceId}/test` declares gets a branch, bar
    // `DEVICE_NO_SMS_PASSWORD`, which this namespace has no string for yet and
    // so falls through to the generic message.
    const getErrorMessage = useCallback(
        (error: unknown): string => {
            const seconds = getRetryAfterSeconds(error);

            // Minting the App Check token happens before the request, so a
            // failure there is not in the endpoint's error union.
            if (
                isFirebaseError(error) ||
                isApiError(error, 'APP_CHECK_FAILED')
            ) {
                return t('shared.errors.app_check_failed');
            }
            if (isApiError(error, 'TOO_MANY_REQUESTS') && seconds !== null) {
                return t('shared.errors.too_many_requests', { seconds });
            }
            if (isApiError(error, 'DEVICE_NO_PHONE')) {
                return t('shared.errors.device_no_phone');
            }
            if (isApiError(error, 'DEVICE_NOT_FOUND')) {
                return t('shared.errors.device_not_found');
            }
            if (isApiError(error, 'BAD_REQUEST')) {
                // The server names the offending field; it is more use than
                // anything generic we could write.
                return error.response?.data.message.join(', ') ?? '';
            }

            return t(getFallbackErrorKey(error));
        },
        [t]
    );

    const mutation = useMutation({
        // An App Check protected route: mint the token first.
        mutationFn: () =>
            getAppCheckTokenHeaders()
                .then((headers) =>
                    openApiRef.current.devices.testDevice(deviceId, { headers })
                )
                .then((response) => response.data.data.statusId),
        // No `onSuccess`: nothing to cache, and the modal reports the result of
        // the test itself as it polls.
        onError: (error) => {
            UIToast.error(getErrorMessage(error));
        }
    });

    return {
        testCallDevice: () => mutation.mutateAsync(),
        loading: mutation.isPending,
        error: mutation.error ? getErrorMessage(mutation.error) : ''
    };
}
